import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, Typography } from "@mui/material"
import type { FC } from "react"
import { Fragment, useState } from "react"
import { useFieldArray, useForm } from "react-hook-form"

import { InventoryProductDescription } from "../../../components"
import { useTransferLookups } from "../hooks/useTransferLookups"
import { useUpdateTransferPalletLpns } from "../hooks/useUpdateTransferPalletLpns"
import { type TransferLpnFormValues, transferLpnFormSchema } from "../schemas/transfer"
import { toTransferLpnFormValues, toTransferLpnUpdateInput } from "../utils/transfer"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { ProductStatusCell } from "@/components/table"
import { FormTextField } from "@/components/UiKit/FormControls/FormTextField"
import type { Transfer } from "@/graphql/codegen/graphql"

interface TransferLpnFormDialogProps {
  transfer: Transfer
  onClose: () => void
  onSubmit: () => void
}

export const TransferLpnFormDialog: FC<TransferLpnFormDialogProps> = ({ transfer, onClose, onSubmit }) => {
  const { updateTransferPalletLpns } = useUpdateTransferPalletLpns()
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const form = useForm<TransferLpnFormValues>({
    resolver: zodResolver(transferLpnFormSchema),
    defaultValues: toTransferLpnFormValues(transfer),
    mode: "onChange",
  })

  const {
    control,
    formState: { isDirty, isValid, isSubmitting },
  } = form

  const canSubmit = isDirty && isValid && !isSubmitting

  const { fields: pallets } = useFieldArray({ control, name: "pallets" })

  const { productsByReferenceNbr } = useTransferLookups(transfer)

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  const handleSubmit = form.handleSubmit(async (formValues) => {
    const isSuccess = await updateTransferPalletLpns(toTransferLpnUpdateInput(formValues, transfer.transferId))

    if (isSuccess) {
      onSubmit()
    }
  })

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  return (
    <Dialog open onClose={handleCancel} maxWidth='md' fullWidth data-testid='transfer.lpn-form-dialog'>
      <DialogHeader title='Edit LPN' onClose={handleCancel} />
      <DialogContent className='grid grid-cols-[auto_auto_1fr_224px] items-start gap-x-8 gap-y-7 p-10'>
        {pallets.map((pallet, index) => {
          const product = productsByReferenceNbr.get(pallet.referenceNbr)!

          return (
            <Fragment key={pallet.id}>
              <ProductStatusCell product={product} className='mt-[14px]' />
              <InventoryProductDescription product={product} className='mt-[14px]' />
              <div className='mt-[14px] flex flex-col gap-y-1'>
                <Typography variant='caption'>Reference #</Typography>
                <Typography variant='body2' className='text-gray-700'>
                  {pallet.referenceNbr}
                </Typography>
              </div>
              <FormTextField
                control={control}
                name={`pallets.${index}.lpn`}
                label='LPN'
                size='small'
                data-testid={`transfer.lpn-form-dialog.lpn-field-${pallet.referenceNbr}`}
              />
            </Fragment>
          )
        })}
      </DialogContent>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button appearance='outlined' onClick={handleCancel} data-testid='transfer.lpn-form-dialog.cancel-button'>
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          onClick={handleSubmit}
          data-testid='transfer.lpn-form-dialog.save-button'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
